import {
  getClientGoalsOptions,
  getUserCoachingAssignmentOptions,
  getUserCoachingLogsOptions,
  getUserDetailedActivityOptions,
} from "@/actions/admin/queries";
import {
  getUserDetailedActivityFn,
  getUserProgramReportFn,
  updateClientGoalsFn,
  updateCoachingAssignmentFn,
  updateUserRoleFn,
} from "@/actions/admin/server";
import { getAdminBodyMeasurementsOptions } from "@/actions/body-measurements/handler";
import {
  assignClientFn,
  removeCoachingClientFn,
} from "@/actions/coaching/mutations";
import { sendUserNotificationFn } from "@/actions/push/handler";
import { getAdminVolumeCheckinsOptions } from "@/actions/volume-checkins/handler";
import type { SelectClientGoal } from "@/api/db/schema/client-goals";
import { ClientWeeklyScheduleManager } from "@/components/admin/ClientWeeklyScheduleManager";
import { UrlPickerInput } from "@/components/admin/url-picker-input";
import { BodyMeasurementHistory } from "@/components/coaching/BodyMeasurementsCard";
import { VolumeCalibrationHistory } from "@/components/coaching/VolumeCalibrationCard";
import { WorkoutMealHistory } from "@/components/coaching/WorkoutMealHistory";
import { WorkoutPosingHistory } from "@/components/coaching/WorkoutPosingHistory";
import { RoadmapTab } from "@/components/profile";
import { HistoryChart } from "@/components/training/HistoryChart";
import { TopSetProgressPanel } from "@/components/training/TopSetProgressPanel";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import {
  Activity,
  ArrowLeft,
  Beef,
  Bell,
  Calendar,
  Download,
  Droplets,
  Flame,
  Loader2,
  Mail,
  Moon,
  PlayCircle,
  Scale,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  User,
  Zap,
} from "lucide-react";
import * as React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/users/$userId")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context, params }) => {
    // Sadece ilk sayfayi load ediyoruz
    await context.queryClient.ensureQueryData({
      ...getUserDetailedActivityOptions(params.userId, 20, 0),
    });
  },
  component: AdminUserDetailContent,
});

interface WorkoutLog {
  logId: string;
  userId: string;
  dayId: string | null;
  exerciseId: string | null;
  setNumber: number | null;
  completedWeight: number | null;
  completedReps: number | null;
  completedRPE: number | null;
  loggedAt: Date;
  notes: string | null;
  weekNumber: number | null;
  dayNumber: number | null;
  dayName: string | null;
  exerciseName: string | null;
  videoUrl: string | null;
  videoThumbnailUrl: string | null;
  videoProcessingStatus: string | null;
}

interface ProgressData {
  date: Date;
  e1RM: number;
  exerciseId: string | null;
  exerciseName: string | null;
  weekNumber: number | null;
  dayNumber: number | null;
}

interface WorkoutSession {
  date: Date;
  dayId: string | null;
  weekNumber: number | null;
  dayNumber: number | null;
  dayName: string | null;
  sets: WorkoutLog[];
}

function UserSendNotificationDialog({
  userId,
  userName,
  hasPushSubscription,
  pushSubscriptionCount = 0,
}: {
  userId: string;
  userName?: string | null;
  hasPushSubscription?: boolean;
  pushSubscriptionCount?: number;
}) {
  const [open, setOpen] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [url, setUrl] = React.useState("");

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await sendUserNotificationFn({
        data: {
          userId,
          title: title.trim(),
          body: body.trim(),
          url: url.trim() || undefined,
        },
      });
      if (res.status !== 200) {
        throw new Error("Bildirim gönderilemedi");
      }
      return res.body;
    },
    onSuccess: (res) => {
      if (res.sent && res.sent > 0) {
        toast.success(`Bildirim başarıyla gönderildi (${res.sent} cihaz)`);
      } else {
        toast.warning(
          "Kullanıcının aktif bir push bildirimi aboneliği bulunmuyor veya bildirim iletilemedi.",
        );
      }
      setTitle("");
      setBody("");
      setUrl("");
      setOpen(false);
    },
    onError: (err: any) => {
      toast.error(err.message || "Bildirim gönderilirken bir hata oluştu.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    sendMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5 text-xs">
          <Bell className="h-3.5 w-3.5 text-primary" />
          <span>Bildirim Gönder</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <DialogTitle>Özel Bildirim Gönder</DialogTitle>
            {hasPushSubscription ? (
              <Badge
                variant="outline"
                className="border-emerald-500/30 bg-emerald-500/10 font-medium text-[10px] text-emerald-600 dark:text-emerald-400"
              >
                Bildirimler Açık ({pushSubscriptionCount} cihaz)
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="border-amber-500/30 bg-amber-500/10 font-medium text-[10px] text-amber-600 dark:text-amber-400"
              >
                Bildirimler Kapalı
              </Badge>
            )}
          </div>
          <DialogDescription>
            {userName ? `${userName} kullanıcısına` : "Kullanıcıya"} anlık push
            bildirimi gönderin.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="user-notif-title" className="text-xs">
              Başlık
            </Label>
            <Input
              id="user-notif-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Örn: Son Hafta Hatırlatması"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-notif-body" className="text-xs">
              Mesaj
            </Label>
            <Textarea
              id="user-notif-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Örn: Ücretsiz programının son haftasındasın..."
              rows={3}
              required
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="user-notif-url" className="text-xs">
                Yönlendirme Linki (Opsiyonel)
              </Label>
              <span className="text-[10px] text-muted-foreground">
                Simgeye tıklayarak sayfa seçin
              </span>
            </div>
            <UrlPickerInput
              value={url}
              onChange={setUrl}
              placeholder="Örn: /training veya özel yönlendirme"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              İptal
            </Button>
            <Button type="submit" disabled={sendMutation.isPending}>
              {sendMutation.isPending ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdminUserDetailContent() {
  const { userId } = Route.useParams();
  const { user: currentAdmin } = Route.useRouteContext();
  const PAGE_SIZE = 20;
  const queryClient = useQueryClient();
  const { data: topSetAssignmentData } = useQuery(
    getUserCoachingAssignmentOptions(userId),
  );
  const hasTopSetTracking = Boolean(
    topSetAssignmentData?.body?.status === "active" &&
      topSetAssignmentData.body.hasTopSetTracking,
  );

  const roleMutation = useMutation({
    mutationFn: async (role: "user" | "admin" | "client" | "author") => {
      const res = await updateUserRoleFn({ data: { userId, role } });
      if (res.status !== 200)
        throw new Error(res.body?.message || "Hata oluştu");
      return res.body;
    },
    onSuccess: () => {
      toast.success("Kullanıcı rolü güncellendi");
      queryClient.invalidateQueries({
        queryKey: ["admin", "user-activity", userId, "infinite"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "users-progress"],
      });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["admin", "user-activity", userId, "infinite"],
      queryFn: ({ pageParam = 0 }) =>
        getUserDetailedActivityFn({
          data: {
            userId,
            limit: PAGE_SIZE,
            offset: (pageParam as number) * PAGE_SIZE,
          },
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.body.hasMore ? allPages.length : undefined;
      },
      select: (data) => {
        const allLogs = data.pages.flatMap((page) => page.body.logs);
        const progressData = data.pages[0]?.body.progressData ?? [];

        // sessionList logic
        const mergedSessions: WorkoutSession[] = [];
        allLogs.forEach((log) => {
          const existingSession = mergedSessions.find(
            (s: WorkoutSession) => s.dayId === log.dayId,
          );
          if (existingSession) {
            existingSession.sets.unshift(log);
          } else {
            mergedSessions.push({
              date: log.loggedAt,
              dayId: log.dayId,
              weekNumber: log.weekNumber,
              dayNumber: log.dayNumber,
              dayName: log.dayName,
              sets: [log],
            });
          }
        });

        const exercisesMap = new Map<string, string>();
        progressData.forEach((p: ProgressData) => {
          if (
            p.exerciseId &&
            p.exerciseName &&
            !exercisesMap.has(p.exerciseId)
          ) {
            exercisesMap.set(p.exerciseId, p.exerciseName);
          }
        });
        const exercisesInChart = Array.from(exercisesMap.entries()).map(
          ([id, name]) => ({ id, name }),
        );

        return {
          progressData,
          dailyMetrics: data.pages[0]?.body.dailyMetrics ?? [],
          goals: data.pages[0]?.body.goals ?? null,
          sessionList: mergedSessions,
          logs: allLogs,
          exercisesInChart,
          user: data.pages[0]?.body.user,
          programReports: data.pages[0]?.body.programReports ?? [],
        };
      },
      enabled: !!userId,
    });

  const [selectedExerciseId, setSelectedExerciseId] =
    React.useState<string>("all");
  const [downloadingProgramId, setDownloadingProgramId] = React.useState<
    string | null
  >(null);
  const [activeVideo, setActiveVideo] = React.useState<{
    url: string;
    exerciseName: string;
    setNumber: number;
    weekNumber?: number | null;
    reps?: number | null;
  } | null>(null);

  const [activeChart, setActiveChart] = React.useState<
    | "perf"
    | "weight"
    | "calories"
    | "protein"
    | "carbs"
    | "fats"
    | "sleepHours"
    | "stressLevel"
    | "energyLevel"
    | "steps"
  >("perf");

  const {
    progressData = [],
    dailyMetrics = [],
    goals = null,
    sessionList = [],
    logs = [],
    exercisesInChart = [],
    programReports = [],
  } = (data as any) || {};

  const handleDownloadProgramReport = async (programId: string) => {
    try {
      setDownloadingProgramId(programId);
      const result = await getUserProgramReportFn({
        data: { userId, programId },
      });

      if (result.status !== 200 || !("logs" in result.body)) {
        throw new Error("Program raporu alınamadı");
      }

      const { logs, programName, ...reportOptions } = result.body;
      const { downloadWorkoutReport } = await import(
        "@/utils/workout-export-utils"
      );
      await downloadWorkoutReport(logs, programName, reportOptions);
    } catch (error) {
      console.error(error);
      toast.error("Program raporu indirilemedi");
    } finally {
      setDownloadingProgramId(null);
    }
  };

  const filteredProgressData = React.useMemo(() => {
    if (selectedExerciseId === "all") return progressData;
    return progressData.filter(
      (p: ProgressData) => p.exerciseId === selectedExerciseId,
    );
  }, [progressData, selectedExerciseId]);

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-5xl space-y-6 px-4 py-24 font-sans">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-5xl space-y-6 px-4 py-24 font-sans">
      {/* Header & Back Button */}
      <div className="space-y-4">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="h-8 px-2 hover:bg-muted"
        >
          <Link
            to="/admin/users"
            className="flex items-center gap-1.5 font-medium text-xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Kullanıcılara Dön
          </Link>
        </Button>

        <div className="flex flex-col justify-between gap-4 border-border/40 border-b pb-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-border/60 bg-muted">
              <User className="h-7 w-7 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="truncate font-bold text-2xl text-foreground tracking-tight">
                {data?.user?.userName || "İsimsiz Kullanıcı"}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-muted-foreground text-sm">
                <div className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{data?.user?.email}</span>
                </div>
                {data?.user?.hasPushSubscription ? (
                  <Badge
                    variant="outline"
                    className="gap-1 border-emerald-500/30 bg-emerald-500/10 font-medium text-[11px] text-emerald-600 dark:text-emerald-400"
                  >
                    <Bell className="h-3 w-3" />
                    <span>
                      Bildirimler Açık ({data.user.pushSubscriptionCount} cihaz)
                    </span>
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="gap-1 border-amber-500/30 bg-amber-500/10 font-medium text-[11px] text-amber-600 dark:text-amber-400"
                  >
                    <Bell className="h-3 w-3 opacity-50" />
                    <span>Bildirimler Kapalı</span>
                  </Badge>
                )}
                {(() => {
                  const consents = (data?.user as any)?.legalConsents;
                  const hasConsented = Boolean(
                    consents?.kvkk?.accepted &&
                      consents?.workoutVideoConsent?.accepted,
                  );
                  return hasConsented ? (
                    <Badge
                      variant="outline"
                      className="gap-1 border-emerald-500/30 bg-emerald-500/10 font-medium text-[11px] text-emerald-600 dark:text-emerald-400"
                    >
                      <ShieldCheck className="h-3 w-3" />
                      <span>KVKK & Video İzni Onaylı</span>
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="gap-1 border-amber-500/30 bg-amber-500/10 font-medium text-[11px] text-amber-600 dark:text-amber-400"
                    >
                      <ShieldAlert className="h-3 w-3" />
                      <span>Yasal Onay Bekliyor</span>
                    </Badge>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label
              htmlFor="user-role-select"
              className="shrink-0 font-bold text-[10px] text-muted-foreground uppercase tracking-wider"
            >
              Sistem Rolü
            </Label>
            <Select
              value={data?.user?.role || "user"}
              onValueChange={(val: any) => roleMutation.mutate(val)}
              disabled={roleMutation.isPending}
            >
              <SelectTrigger id="user-role-select" className="h-9 w-32 text-xs">
                <SelectValue placeholder="Rol Seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user" className="text-xs">
                  User
                </SelectItem>
                <SelectItem value="client" className="text-xs">
                  Client
                </SelectItem>
                <SelectItem value="author" className="text-xs">
                  Author
                </SelectItem>
                <SelectItem value="admin" className="text-xs">
                  Admin
                </SelectItem>
              </SelectContent>
            </Select>

            <UserSendNotificationDialog
              userId={userId}
              userName={data?.user?.userName}
              hasPushSubscription={data?.user?.hasPushSubscription}
              pushSubscriptionCount={data?.user?.pushSubscriptionCount}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-64 w-full rounded-xl" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
        </div>
      ) : (
        <Tabs defaultValue="chart" className="w-full">
          <TabsList className="mb-6 h-10 w-full justify-start gap-4 rounded-none border-border/40 border-b bg-transparent p-0">
            <TabsTrigger
              value="chart"
              className="rounded-none border-transparent border-b-2 px-4 py-2 font-medium text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              Gelişim Analizi
            </TabsTrigger>
            <TabsTrigger
              value="list"
              className="rounded-none border-transparent border-b-2 px-4 py-2 font-medium text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              Antrenman Geçmişi
            </TabsTrigger>
            <TabsTrigger
              value="coaching"
              className="rounded-none border-transparent border-b-2 px-4 py-2 font-medium text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              Yönetim Paneli
            </TabsTrigger>
            <TabsTrigger
              value="roadmap"
              className="rounded-none border-transparent border-b-2 px-4 py-2 font-medium text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              Yol Haritası
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chart" className="space-y-6 outline-none">
            {hasTopSetTracking && <TopSetProgressPanel userId={userId} />}
            <div className="flex flex-wrap gap-2">
              {[
                { id: "perf", label: "Performans", icon: TrendingUp },
                { id: "weight", label: "Ağırlık", icon: Scale },
                { id: "calories", label: "Kalori", icon: Flame },
                { id: "protein", label: "Protein", icon: Beef },
                { id: "carbs", label: "Karb", icon: Flame },
                { id: "fats", label: "Yağ", icon: Droplets },
                { id: "sleepHours", label: "Uyku", icon: Moon },
                { id: "stressLevel", label: "Stres", icon: Activity },
                { id: "energyLevel", label: "Enerji", icon: Zap },
                { id: "steps", label: "Aktivite", icon: Activity },
              ].map((item) => (
                <Button
                  key={item.id}
                  variant={activeChart === item.id ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setActiveChart(item.id as any)}
                  className={cn(
                    "h-8 rounded-lg px-3 font-medium text-[11px] transition-colors",
                    activeChart === item.id
                      ? "bg-primary/10 text-primary hover:bg-primary/20"
                      : "text-muted-foreground",
                  )}
                >
                  <item.icon className="mr-1.5 h-3.5 w-3.5" />
                  {item.label}
                </Button>
              ))}
            </div>

            <Card className="rounded-xl border border-border/60 bg-card/30 p-4 shadow-none sm:p-6">
              <div className="mb-6 flex items-center justify-between">
                {activeChart === "perf" && (
                  <div className="flex items-center gap-2">
                    {exercisesInChart.length > 0 && (
                      <Select
                        value={selectedExerciseId}
                        onValueChange={setSelectedExerciseId}
                      >
                        <SelectTrigger className="h-8 w-[180px] border-border/60 bg-background/50 font-medium text-xs">
                          <SelectValue placeholder="Egzersiz Seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all" className="text-xs">
                            Tüm Egzersizler (Genel)
                          </SelectItem>
                          {exercisesInChart.map(
                            (ex: { id: string; name: string }) => (
                              <SelectItem
                                key={ex.id}
                                value={ex.id}
                                className="text-xs"
                              >
                                {ex.name}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                <div className="ml-auto rounded-md bg-muted/50 px-2 py-1 font-bold text-[10px] text-muted-foreground uppercase tracking-widest">
                  Analiz Görünümü
                </div>
              </div>

              <div className="min-h-[350px] w-full">
                {activeChart === "perf" ? (
                  <HistoryChart data={filteredProgressData} />
                ) : activeChart === "weight" ? (
                  <MetricChart
                    data={dailyMetrics}
                    dataKey="weight"
                    label="Vücut Ağırlığı"
                    unit="kg"
                    target={goals?.weightTarget}
                    color="hsl(var(--primary))"
                  />
                ) : activeChart === "calories" ? (
                  <MetricChart
                    data={dailyMetrics}
                    dataKey="calories"
                    label="Kalori Alımı"
                    unit="kcal"
                    target={goals?.caloriesTarget}
                    color="#ef4444"
                  />
                ) : activeChart === "protein" ? (
                  <MetricChart
                    data={dailyMetrics}
                    dataKey="protein"
                    label="Protein Tüketimi"
                    unit="g"
                    target={goals?.proteinTarget}
                    color="#3b82f6"
                  />
                ) : activeChart === "carbs" ? (
                  <MetricChart
                    data={dailyMetrics}
                    dataKey="carbs"
                    label="Karbonhidrat Tüketimi"
                    unit="g"
                    target={goals?.carbsTarget}
                    color="#f59e0b"
                  />
                ) : activeChart === "fats" ? (
                  <MetricChart
                    data={dailyMetrics}
                    dataKey="fats"
                    label="Yağ Tüketimi"
                    unit="g"
                    target={goals?.fatsTarget}
                    color="#10b981"
                  />
                ) : activeChart === "sleepHours" ? (
                  <MetricChart
                    data={dailyMetrics}
                    dataKey="sleepHours"
                    label="Uyku Süresi"
                    unit="sa"
                    target={goals?.sleepHoursTarget}
                    color="#8b5cf6"
                  />
                ) : activeChart === "stressLevel" ? (
                  <MetricChart
                    data={dailyMetrics}
                    dataKey="stressLevel"
                    label="Stres Düzeyi"
                    unit="/10"
                    color="#ec4899"
                  />
                ) : activeChart === "energyLevel" ? (
                  <MetricChart
                    data={dailyMetrics}
                    dataKey="energyLevel"
                    label="Enerji Düzeyi"
                    unit="/10"
                    color="#eab308"
                  />
                ) : (
                  <MetricChart
                    data={dailyMetrics}
                    dataKey="steps"
                    label="Adım Sayısı"
                    unit="adım"
                    target={goals?.stepsTarget}
                    color="#10b981"
                  />
                )}
              </div>
            </Card>

            <WorkoutMealHistory clientId={userId} />

            <WorkoutPosingHistory clientId={userId} />

            <div className="rounded-xl border-primary/20 border-l-4 bg-primary/5 px-6 py-4">
              <p className="mb-1 font-semibold text-primary/80 text-xs uppercase leading-relaxed tracking-wider">
                Analiz Özeti
              </p>
              <p className="font-medium text-[11px] text-muted-foreground/80 leading-relaxed">
                {activeChart === "perf"
                  ? "Seçili egzersiz için tahmini 1RM (e1RM) gelişimi. Tepe noktaları en yüksek performansı, son nokta ise güncel seviyeyi temsil eder."
                  : activeChart === "weight"
                    ? "Sabah aç karnına girilen vücut ağırlığı verileri. Hedef çizgisine olan yakınlık diyet başarısını gösterir."
                    : activeChart === "calories"
                      ? "Günlük toplam kalori alımı takibi."
                      : activeChart === "protein"
                        ? "Günlük toplam protein alımı takibi."
                        : activeChart === "carbs"
                          ? "Günlük toplam karbonhidrat alımı takibi."
                          : activeChart === "fats"
                            ? "Günlük toplam yağ alımı takibi."
                            : activeChart === "sleepHours"
                              ? "Günlük toplam uyku süresi takibi."
                              : activeChart === "stressLevel"
                                ? "Günlük hissedilen stres düzeyi takibi (1-10 arası)."
                                : activeChart === "energyLevel"
                                  ? "Günlük genel enerji düzeyi takibi (1-10 arası)."
                                  : "Günlük toplam hareketlilik ve adım verileri."}
              </p>
            </div>
          </TabsContent>

          <TabsContent value="list" className="space-y-4 outline-none">
            <Card className="rounded-xl border border-border/60 bg-card/30 p-4 shadow-none sm:p-5">
              <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                <div>
                  <h3 className="font-bold text-sm">Program Sonuç Raporları</h3>
                  <p className="mt-1 text-muted-foreground text-xs">
                    Aktif veya geçmiş programların tüm antrenman kayıtlarını
                    Excel olarak indirin.
                  </p>
                </div>
                <Badge variant="outline" className="w-fit text-[10px]">
                  {programReports.length} PROGRAM
                </Badge>
              </div>

              {programReports.length === 0 ? (
                <div className="rounded-lg border border-border/50 border-dashed p-6 text-center text-muted-foreground text-xs">
                  Kullanıcıya atanmış bir program bulunmuyor.
                </div>
              ) : (
                <div className="space-y-2">
                  {programReports.map((program: any) => {
                    const statusLabel =
                      program.status === "active"
                        ? "Aktif"
                        : program.status === "completed"
                          ? "Tamamlandı"
                          : "Duraklatıldı";
                    const isDownloading =
                      downloadingProgramId === program.programId;

                    return (
                      <div
                        key={program.userProgramId}
                        className="flex flex-col gap-3 rounded-lg border border-border/50 bg-background/40 p-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate font-semibold text-sm">
                              {program.programName}
                            </p>
                            <Badge
                              variant="secondary"
                              className="h-5 text-[9px] uppercase"
                            >
                              {statusLabel}
                            </Badge>
                          </div>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {program.logCount > 0
                              ? `${program.logCount} set kaydı · Hafta ${program.currentWeek}/${program.durationWeeks}`
                              : "Henüz antrenman kaydı yok"}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          disabled={program.logCount === 0 || isDownloading}
                          onClick={() =>
                            handleDownloadProgramReport(program.programId)
                          }
                        >
                          {isDownloading ? (
                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Download className="mr-2 h-3.5 w-3.5" />
                          )}
                          {isDownloading ? "Hazırlanıyor" : "Excel İndir"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {logs.length === 0 ? (
              <Card className="flex flex-col items-center justify-center border-border/60 border-dashed bg-transparent p-12 text-center shadow-none">
                <p className="text-muted-foreground text-sm">
                  Henüz antrenman kaydı bulunmuyor.
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                <Accordion type="multiple" className="w-full space-y-3">
                  {sessionList.map((session: WorkoutSession) => {
                    const totalVolume = session.sets.reduce(
                      (acc: number, set: WorkoutLog) =>
                        acc +
                        (set.completedWeight || 0) * (set.completedReps || 0),
                      0,
                    );

                    return (
                      <AccordionItem
                        key={session.date.toString()}
                        value={session.date.toString()}
                        className="overflow-hidden rounded-lg border border-border/60 bg-card/30 px-2 shadow-none"
                      >
                        <AccordionTrigger className="px-2 py-4 hover:no-underline">
                          <div className="flex w-full items-center justify-between pr-4">
                            <div className="text-left">
                              <p className="font-semibold text-sm">
                                {session.weekNumber
                                  ? `Hafta ${session.weekNumber}, Gün ${session.dayNumber}`
                                  : "Özel Antrenman"}
                              </p>
                              <p className="mt-0.5 font-medium text-[10px] text-muted-foreground uppercase">
                                {format(
                                  new Date(session.date),
                                  "d MMM yyyy, EEEE",
                                  { locale: tr },
                                )}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="rounded bg-muted px-2 py-0.5 font-bold text-[10px] text-muted-foreground">
                                {session.sets.length} SET
                              </span>
                              {totalVolume > 0 && (
                                <span className="rounded bg-primary/5 px-2 py-0.5 font-bold text-[10px] text-primary">
                                  {totalVolume} KG
                                </span>
                              )}
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-2 pt-0 pb-4">
                          <div className="overflow-hidden rounded-md border border-border/40">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/50 font-semibold text-[9px] text-muted-foreground uppercase">
                                <tr>
                                  <th className="px-3 py-2 text-left">
                                    Egzersiz
                                  </th>
                                  <th className="px-2 py-2 text-center">Yük</th>
                                  <th className="px-2 py-2 text-center">Tek</th>
                                  <th className="px-2 py-2 text-center">RPE</th>
                                  <th className="px-2 py-2 text-right">
                                    Video
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border/20">
                                {session.sets.map((set: WorkoutLog) => (
                                  <React.Fragment key={set.logId}>
                                    <tr className="hover:bg-muted/20">
                                      <td className="px-3 py-3 font-medium text-[11px]">
                                        {set.exerciseName || "Egzersiz"}
                                      </td>
                                      <td className="px-2 py-3 text-center font-semibold tabular-nums">
                                        {set.completedWeight ?? "-"}{" "}
                                        <span className="text-[9px] opacity-40">
                                          kg
                                        </span>
                                      </td>
                                      <td className="px-2 py-3 text-center font-medium">
                                        {set.completedReps ?? "-"}
                                      </td>
                                      <td className="px-2 py-3 text-center font-medium">
                                        {set.completedRPE ? (
                                          <span className="font-bold text-[10px] text-primary">
                                            {set.completedRPE}
                                          </span>
                                        ) : (
                                          "-"
                                        )}
                                      </td>
                                      <td className="px-2 py-1 text-right">
                                        {set.videoUrl &&
                                        set.videoProcessingStatus === "done" ? (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 rounded-full text-primary hover:bg-primary/10 hover:text-primary"
                                            onClick={() =>
                                              setActiveVideo({
                                                url: set.videoUrl!,
                                                exerciseName:
                                                  set.exerciseName ||
                                                  "Egzersiz",
                                                setNumber: set.setNumber || 1,
                                                weekNumber: set.weekNumber,
                                                reps: set.completedReps,
                                              })
                                            }
                                          >
                                            <PlayCircle className="h-5 w-5" />
                                          </Button>
                                        ) : set.videoProcessingStatus ===
                                          "processing" ? (
                                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                        ) : null}
                                      </td>
                                    </tr>
                                    {set.notes && (
                                      <tr className="bg-muted/10">
                                        <td
                                          colSpan={5}
                                          className="border-border/5 border-t px-3 py-2 text-[10px] text-muted-foreground italic"
                                        >
                                          Not: {set.notes}
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>

                {hasNextPage && (
                  <div className="flex justify-center pt-4">
                    <Button
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      variant="outline"
                      size="sm"
                      className="h-8 rounded-full px-6 text-xs"
                    >
                      {isFetchingNextPage
                        ? "Yükleniyor..."
                        : "Daha Fazla Göster"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="coaching" className="outline-none">
            <CoachingPanel
              userId={userId}
              adminUserId={currentAdmin?.userId ?? ""}
            />
          </TabsContent>

          <TabsContent value="roadmap" className="space-y-6 outline-none">
            <RoadmapTab userId={userId} canEdit />
          </TabsContent>
        </Tabs>
      )}

      {/* Video Playback Modal */}
      <Dialog
        open={!!activeVideo}
        onOpenChange={(open) => !open && setActiveVideo(null)}
      >
        <DialogContent className="flex max-h-[95vh] w-[95vw] max-w-4xl flex-col overflow-hidden rounded-xl border bg-background p-0 sm:w-full">
          <DialogHeader className="border-b px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-1 text-left sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="min-w-0 flex-1">
                <h3 className="truncate font-bold text-foreground text-lg sm:text-xl">
                  {activeVideo?.exerciseName || "Egzersiz"}
                </h3>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold text-[10px] text-muted-foreground uppercase tracking-wide sm:text-xs">
                  {activeVideo?.weekNumber && (
                    <span className="flex items-center gap-1">
                      <span className="opacity-50">Hafta</span>
                      <span className="text-foreground">
                        {activeVideo.weekNumber}
                      </span>
                    </span>
                  )}
                  <span className="h-1 w-1 rounded-full bg-border" />
                  <span className="flex items-center gap-1">
                    <span className="opacity-50">Set</span>
                    <span className="text-foreground">
                      {activeVideo?.setNumber}
                    </span>
                  </span>
                  {activeVideo?.reps && (
                    <>
                      <span className="h-1 w-1 rounded-full bg-border" />
                      <span className="flex items-center gap-1">
                        <span className="text-foreground">
                          {activeVideo.reps}
                        </span>
                        <span className="opacity-50">Tekrar</span>
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="hidden sm:block">
                <div className="rounded-full bg-muted px-3 py-1 font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                  Video Kaydı
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="relative flex min-h-[30vh] w-full flex-1 items-center justify-center bg-black/95">
            {activeVideo && (
              <video
                src={activeVideo.url}
                className="max-h-[60vh] w-full object-contain sm:max-h-[75vh]"
                controls
                autoPlay
                playsInline
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── MetricChart Component ────────────────────────────── */
function MetricChart({
  data,
  dataKey,
  label,
  unit,
  target,
  color,
  secondaryData = [],
}: {
  data: any[];
  dataKey: string;
  label: string;
  unit: string;
  target?: number | null;
  color: string;
  secondaryData?: { key: string; label: string; color: string }[];
}) {
  const chartData = React.useMemo(() => {
    return [...data].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [data]);

  const yDomain = React.useMemo(() => {
    if (chartData.length === 0) return ["auto", "auto"];
    const values = chartData
      .map((d) => Number(d[dataKey]))
      .filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
    if (values.length === 0) return ["auto", "auto"];
    const min = Math.min(...values);
    const max = Math.max(...values);

    if (dataKey === "stressLevel" || dataKey === "energyLevel") {
      return [0, 10];
    }
    if (dataKey === "sleepHours") {
      const dMin = Math.max(0, Math.floor(min - 1));
      const dMax = Math.ceil(max + 1);
      return [dMin, dMax];
    }
    if (dataKey === "calories") {
      const dMin = Math.max(0, Math.floor(min - 200));
      const dMax = Math.ceil(max + 200);
      return [dMin, dMax];
    }
    if (dataKey === "steps") {
      const dMin = Math.max(0, Math.floor(min - 1000));
      const dMax = Math.ceil(max + 1000);
      return [dMin, dMax];
    }
    if (dataKey === "protein" || dataKey === "carbs" || dataKey === "fats") {
      const dMin = Math.max(0, Math.floor(min - 10));
      const dMax = Math.ceil(max + 10);
      return [dMin, dMax];
    }
    // Ağırlık / weight
    const dMin = Math.max(0, Math.floor(min - 3));
    const dMax = Math.ceil(max + 3);
    return [dMin, dMax];
  }, [chartData, dataKey]);

  if (chartData.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <p className="text-muted-foreground text-xs">Veri bulunmuyor.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <span className="font-bold text-[10px] text-muted-foreground uppercase">
          {label} ({unit})
        </span>
        {target && (
          <span className="font-bold text-[10px] text-primary/60 uppercase">
            HEDEF: {target}
          </span>
        )}
      </div>
      <div className="min-h-0 w-full flex-1">
        <ChartContainer config={{}} className="h-full w-full">
          <AreaChart
            data={chartData}
            margin={{ left: 0, right: 48, top: 20, bottom: 20 }}
          >
            <defs>
              <linearGradient
                id={`fill-${dataKey}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={color} stopOpacity={0.2} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              strokeDasharray="4 4"
              strokeOpacity={0.05}
            />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tickFormatter={(v) =>
                format(new Date(v), "d MMM", { locale: tr })
              }
              className="fill-muted-foreground font-bold text-[9px] uppercase opacity-40"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              domain={yDomain}
              tickMargin={10}
              className="font-bold text-[9px] text-muted-foreground/30"
              width={40}
            />
            <ChartTooltip
              cursor={{
                stroke: color,
                strokeWidth: 1,
                strokeDasharray: "4 4",
                opacity: 0.2,
              }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="min-w-[140px] overflow-hidden rounded-2xl border border-border/50 bg-background/95 p-0 shadow-2xl backdrop-blur-md">
                    <div className="border-border/40 border-b bg-muted/30 px-3 py-2">
                      <div className="whitespace-nowrap font-bold text-[9px] text-muted-foreground/60 uppercase tracking-widest">
                        Veri Kaydı
                      </div>
                      <div className="mt-0.5 font-bold text-foreground/80 text-xs leading-none">
                        {format(new Date(d.date), "d MMMM yyyy", {
                          locale: tr,
                        })}
                      </div>
                    </div>
                    <div className="p-3">
                      {payload.map((p: any) => (
                        <div
                          key={p.dataKey}
                          className="mb-2 flex flex-col last:mb-0"
                        >
                          <span className="mb-0.5 font-bold text-[8px] text-muted-foreground/40 uppercase tracking-widest">
                            {p.name}
                          </span>
                          <div className="flex items-baseline gap-1.5">
                            <span
                              className="font-black text-foreground text-xl tabular-nums leading-none tracking-tighter"
                              style={{ color: p.color }}
                            >
                              {p.value}
                            </span>
                            <span className="font-bold text-[10px] text-muted-foreground uppercase opacity-40">
                              {unit}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }}
            />
            {target && (
              <ReferenceLine
                y={target}
                stroke={color}
                strokeDasharray="4 4"
                strokeOpacity={0.3}
              />
            )}
            <Area
              name={label}
              dataKey={dataKey}
              type="monotone"
              fill={`url(#fill-${dataKey})`}
              stroke={color}
              strokeWidth={2}
              animationDuration={1500}
              dot={false}
            />
            {secondaryData.map((s) => (
              <Area
                key={s.key}
                name={s.label}
                dataKey={s.key}
                type="monotone"
                fill="transparent"
                stroke={s.color}
                strokeWidth={1.5}
                strokeDasharray="3 3"
                dot={false}
              />
            ))}
          </AreaChart>
        </ChartContainer>
      </div>
    </div>
  );
}

function CoachingPanel({
  userId,
  adminUserId,
}: { userId: string; adminUserId: string }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = React.useState<string>("");
  const [hasPosingRoutine, setHasPosingRoutine] =
    React.useState<boolean>(false);
  const [hasTopSetTracking, setHasTopSetTracking] =
    React.useState<boolean>(false);

  const {
    data: goalsData,
    isLoading: goalsLoading,
    isError: goalsError,
  } = useQuery(getClientGoalsOptions(userId));
  const {
    data: assignmentData,
    isLoading: assignmentLoading,
    isError: assignmentError,
  } = useQuery(getUserCoachingAssignmentOptions(userId));
  const {
    data: logsData,
    isLoading: logsLoading,
    isError: logsError,
  } = useQuery(getUserCoachingLogsOptions(userId));
  const { data: bodyMeasurementsData } = useQuery({
    ...getAdminBodyMeasurementsOptions(userId),
    enabled: assignmentData?.body?.status === "active",
  });
  const { data: volumeCheckinsData } = useQuery({
    ...getAdminVolumeCheckinsOptions(userId),
    enabled: assignmentData?.body?.status === "active",
  });

  React.useEffect(() => {
    if (assignmentData?.body) {
      if (assignmentData.body.status) {
        setStatus(assignmentData.body.status);
      }
      setHasPosingRoutine(Boolean(assignmentData.body.hasPosingRoutine));
      setHasTopSetTracking(Boolean(assignmentData.body.hasTopSetTracking));
    }
  }, [assignmentData]);

  const assignMutation = useMutation({
    mutationFn: async (notes: string) => {
      const res = await assignClientFn({ data: { clientId: userId, notes } });
      if (res.status !== 201)
        throw new Error(res.body?.message || "Hata oluştu");
      return res.body;
    },
    onSuccess: () => {
      toast.success("Kullanıcı danışan olarak atandı");
      queryClient.invalidateQueries({
        queryKey: ["admin", "coaching-assignment", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "users-progress"],
      });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      const res = await removeCoachingClientFn({ data: { clientId: userId } });
      if (res.status !== 200)
        throw new Error(res.body?.message || "Hata oluştu");
      return res.body;
    },
    onSuccess: () => {
      toast.success("Kullanıcı danışanlar listesinden çıkarıldı");
      queryClient.invalidateQueries({
        queryKey: ["admin", "coaching-assignment", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "users-progress"],
      });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const goalMutation = useMutation({
    mutationFn: async (goals: any) => {
      const res = await updateClientGoalsFn({ data: { userId, goals } });
      return res;
    },
    onSuccess: () => {
      toast.success("Hedefler güncellendi");
      queryClient.invalidateQueries({
        queryKey: ["admin", "client-goals", userId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "user-activity", userId],
      });
    },
    onError: () => toast.error("Hedefler güncellenirken hata oluştu"),
  });

  const assignmentMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await updateCoachingAssignmentFn({ data: { userId, data } });
      return res;
    },
    onSuccess: () => {
      toast.success("Periyot bilgileri güncellendi");
      queryClient.invalidateQueries({
        queryKey: ["admin", "coaching-assignment", userId],
      });
    },
    onError: () => toast.error("Bilgiler güncellenirken hata oluştu"),
  });

  if (goalsLoading || assignmentLoading || logsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  if (goalsError || assignmentError || logsError) {
    return (
      <div className="p-8 text-center text-destructive">
        Veriler yüklenirken bir hata oluştu.
      </div>
    );
  }

  const goals = (goalsData?.body?.goals as Partial<SelectClientGoal>) || {};
  const assignment = assignmentData?.body;
  const logs = logsData?.body || [];

  if (!assignment) {
    return (
      <Card className="mx-auto max-w-xl rounded-xl border-border/60 bg-transparent p-8 text-center shadow-none">
        <div className="mb-4 flex justify-center">
          <Zap className="h-10 w-10 text-primary opacity-20" />
        </div>
        <h3 className="mb-2 font-bold text-lg">Henüz Danışan Değil</h3>
        <p className="mb-6 text-muted-foreground text-sm">
          Bu kullanıcıyı koçluk sistemine dahil ederek hedeflerini yönetmeye
          başlayabilirsiniz.
        </p>
        <div className="mx-auto max-w-xs space-y-4 text-left">
          <Input
            id="assign-notes"
            placeholder="Başlangıç notu (opsiyonel)"
            className="h-9 text-xs"
          />
          <Button
            onClick={() =>
              assignMutation.mutate(
                (document.getElementById("assign-notes") as HTMLInputElement)
                  ?.value || "",
              )
            }
            disabled={assignMutation.isPending}
            className="h-10 w-full font-semibold"
          >
            {assignMutation.isPending ? "Ekleniyor..." : "Danışan Olarak Ekle"}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {assignment.status === "active" && (
        <ClientWeeklyScheduleManager
          clientId={userId}
          adminUserId={adminUserId}
        />
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="rounded-xl border border-border/60 bg-card/30 p-6 shadow-none">
          <h4 className="mb-4 flex items-center gap-2 font-bold text-sm">
            <Calendar className="h-4 w-4 text-primary" /> Süreç Periyodu
          </h4>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              assignmentMutation.mutate({
                startDate: fd.get("startDate") as string,
                status: status,
                hasPosingRoutine: hasPosingRoutine,
                hasTopSetTracking: hasTopSetTracking,
              });
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <label className="font-bold text-[10px] text-muted-foreground uppercase">
                Başlangıç Tarihi
              </label>
              <Input
                name="startDate"
                type="date"
                defaultValue={
                  assignment?.startDate
                    ? format(new Date(assignment.startDate), "yyyy-MM-dd")
                    : ""
                }
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="font-bold text-[10px] text-muted-foreground uppercase">
                Durum
              </label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Durum Seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active" className="text-sm">
                    Aktif
                  </SelectItem>
                  <SelectItem
                    value="paused"
                    className="text-sm text-yellow-500"
                  >
                    Durduruldu
                  </SelectItem>
                  <SelectItem
                    value="completed"
                    className="text-green-500 text-sm"
                  >
                    Tamamlandı
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bodybuilding Posing Routine Assignment Switch */}
            <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 p-3">
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5 font-semibold text-xs">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                  <span>Bodybuilding Poz Rutini</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Sporcunun antrenman sayfasında poz kaydetmesini sağlar.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={hasPosingRoutine}
                onClick={() => setHasPosingRoutine((prev) => !prev)}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden",
                  hasPosingRoutine ? "bg-amber-500" : "bg-muted-foreground/30",
                )}
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform",
                    hasPosingRoutine ? "translate-x-5" : "translate-x-0",
                  )}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 p-3">
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5 font-semibold text-xs">
                  <Trophy className="h-3.5 w-3.5 text-primary" />
                  <span>Haftalık Top Set Takibi</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Profilde SBD top setlerini ve haftalık total gelişimini açar.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-label="Haftalık top set takibini aç veya kapat"
                aria-checked={hasTopSetTracking}
                onClick={() => setHasTopSetTracking((prev) => !prev)}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden",
                  hasTopSetTracking ? "bg-primary" : "bg-muted-foreground/30",
                )}
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform",
                    hasTopSetTracking ? "translate-x-5" : "translate-x-0",
                  )}
                />
              </button>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                size="sm"
                className="w-full"
                disabled={assignmentMutation.isPending}
              >
                {assignmentMutation.isPending ? "Güncelleniyor..." : "Güncelle"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full border-destructive/35 text-destructive transition-all hover:bg-destructive/10 hover:text-destructive"
                    disabled={removeMutation.isPending}
                  >
                    {removeMutation.isPending
                      ? "Çıkarılıyor..."
                      : "Danışanlıktan Çıkar"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Danışanlıktan Çıkar</AlertDialogTitle>
                    <AlertDialogDescription>
                      Bu kullanıcıyı danışanlar listesinden çıkarmak
                      istediğinize emin misiniz? Bu işlem kullanıcının koçluk
                      panelindeki erişimini sonlandıracaktır.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => removeMutation.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Evet, Çıkar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </form>
        </Card>

        <Card className="rounded-xl border border-border/60 bg-card/30 p-6 shadow-none">
          <h4 className="mb-4 flex items-center gap-2 font-bold text-sm">
            <Target className="h-4 w-4 text-primary" /> Metabolik Hedefler
          </h4>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              goalMutation.mutate({
                stepsTarget: fd.get("stepsTarget")
                  ? Number(fd.get("stepsTarget"))
                  : null,
                caloriesTarget: fd.get("caloriesTarget")
                  ? Number(fd.get("caloriesTarget"))
                  : null,
                proteinTarget: fd.get("proteinTarget")
                  ? Number(fd.get("proteinTarget"))
                  : null,
                carbsTarget: fd.get("carbsTarget")
                  ? Number(fd.get("carbsTarget"))
                  : null,
                fatsTarget: fd.get("fatsTarget")
                  ? Number(fd.get("fatsTarget"))
                  : null,
                weightTarget: fd.get("weightTarget")
                  ? Number(fd.get("weightTarget"))
                  : null,
                sleepHoursTarget: fd.get("sleepHoursTarget")
                  ? Number(fd.get("sleepHoursTarget"))
                  : null,
                goal1: fd.get("goal1") as string,
                goal2: fd.get("goal2") as string,
                goal3: fd.get("goal3") as string,
              });
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="font-bold text-[9px] text-muted-foreground uppercase">
                  Günlük Adım
                </label>
                <Input
                  name="stepsTarget"
                  type="number"
                  defaultValue={goals.stepsTarget || ""}
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <label className="font-bold text-[9px] text-muted-foreground uppercase">
                  Günlük Kalori
                </label>
                <Input
                  name="caloriesTarget"
                  type="number"
                  defaultValue={goals.caloriesTarget || ""}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <label className="font-bold text-[8px] text-muted-foreground uppercase">
                  Protein
                </label>
                <Input
                  name="proteinTarget"
                  type="number"
                  defaultValue={goals.proteinTarget || ""}
                  className="h-8 text-center text-xs"
                />
              </div>
              <div className="space-y-1">
                <label className="font-bold text-[8px] text-muted-foreground uppercase">
                  Karb
                </label>
                <Input
                  name="carbsTarget"
                  type="number"
                  defaultValue={goals.carbsTarget || ""}
                  className="h-8 text-center text-xs"
                />
              </div>
              <div className="space-y-1">
                <label className="font-bold text-[8px] text-muted-foreground uppercase">
                  Yağ
                </label>
                <Input
                  name="fatsTarget"
                  type="number"
                  defaultValue={goals.fatsTarget || ""}
                  className="h-8 text-center text-xs"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="space-y-1">
                <label className="font-bold text-[9px] text-primary/70 uppercase">
                  Hedef Vücut Ağırlığı (KG)
                </label>
                <Input
                  name="weightTarget"
                  type="number"
                  step="0.1"
                  defaultValue={goals.weightTarget || ""}
                  className="h-8 border-primary/20 bg-primary/5 font-bold text-primary text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="font-bold text-[9px] text-muted-foreground uppercase">
                  Hedef Uyku Süresi (Saat)
                </label>
                <Input
                  name="sleepHoursTarget"
                  type="number"
                  step="0.5"
                  defaultValue={goals.sleepHoursTarget || ""}
                  className="h-8 text-xs"
                />
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <label className="font-bold text-[9px] text-muted-foreground uppercase">
                Özel Hedefler & Notlar
              </label>
              <div className="space-y-2">
                <Input
                  name="goal1"
                  placeholder="Hedef 1 (Örn: Haftada 4 antrenman)"
                  defaultValue={goals.goal1 || ""}
                  className="h-8 text-xs"
                />
                <Input
                  name="goal2"
                  placeholder="Hedef 2 (Örn: Uyku düzeni)"
                  defaultValue={goals.goal2 || ""}
                  className="h-8 text-xs"
                />
                <Input
                  name="goal3"
                  placeholder="Hedef 3 (Örn: Su tüketimi)"
                  defaultValue={goals.goal3 || ""}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <Button
              type="submit"
              size="sm"
              className="w-full"
              disabled={goalMutation.isPending}
            >
              {goalMutation.isPending ? "Kaydediliyor..." : "Hedefleri Kaydet"}
            </Button>
          </form>
        </Card>
      </div>

      <Card className="overflow-hidden rounded-xl border border-border/60 bg-card/30 shadow-none">
        <div className="flex items-center justify-between border-border/40 border-b bg-muted/20 p-4">
          <h4 className="flex items-center gap-2 font-bold text-sm">
            <Scale className="h-4 w-4 text-primary" /> Vücut Ölçümleri
          </h4>
          <span className="font-medium text-[10px] text-muted-foreground uppercase">
            Danışan tarafından yönetilir
          </span>
        </div>
        <div className="p-4">
          <BodyMeasurementHistory
            measurements={bodyMeasurementsData?.body.measurements ?? []}
            readOnly
          />
        </div>
      </Card>

      <Card className="overflow-hidden rounded-xl border border-border/60 bg-card/30 shadow-none">
        <div className="flex items-center justify-between border-border/40 border-b bg-muted/20 p-4">
          <h4 className="flex items-center gap-2 font-bold text-sm">
            <Activity className="h-4 w-4 text-primary" /> Kişisel Hacim Takibi
          </h4>
          <span className="font-medium text-[10px] text-muted-foreground uppercase">
            Danışan geri bildirimi
          </span>
        </div>
        <div className="p-4">
          <VolumeCalibrationHistory
            checkins={volumeCheckinsData?.body.checkins ?? []}
          />
        </div>
      </Card>

      <Card className="overflow-hidden rounded-xl border border-border/60 bg-card/30 shadow-none">
        <div className="flex items-center justify-between border-border/40 border-b bg-muted/20 p-4">
          <h4 className="flex items-center gap-2 font-bold text-sm">
            <TrendingUp className="h-4 w-4 text-primary" /> Günlük Metrikler
          </h4>
          <span className="font-medium text-[10px] text-muted-foreground uppercase">
            Son 30 Log
          </span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/10">
              <TableRow className="border-border/40 border-b font-bold text-[10px] text-muted-foreground uppercase hover:bg-transparent">
                <TableHead className="px-4 py-4">Tarih</TableHead>
                <TableHead className="text-center">Kilo</TableHead>
                <TableHead className="text-center">Kalori</TableHead>
                <TableHead className="text-center">Protein</TableHead>
                <TableHead className="text-center">Karb</TableHead>
                <TableHead className="text-center">Yağ</TableHead>
                <TableHead className="text-center">Adım</TableHead>
                <TableHead className="px-4">Not</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="h-32 text-center text-muted-foreground text-xs"
                  >
                    Henüz veri girişi yok.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log: any) => (
                  <TableRow
                    key={log.id}
                    className="border-border/10 border-b text-xs last:border-0 hover:bg-muted/10"
                  >
                    <TableCell className="whitespace-nowrap px-4 py-3 font-medium">
                      {format(new Date(log.date), "d MMM, EEE", { locale: tr })}
                    </TableCell>
                    <TableCell className="text-center font-semibold tabular-nums">
                      {log.weight || "-"}{" "}
                      <span className="text-[9px] opacity-40">kg</span>
                    </TableCell>
                    <TableCell className="text-center font-semibold text-primary/70 tabular-nums">
                      {log.calories || "-"}
                    </TableCell>
                    <TableCell className="text-center font-semibold text-blue-500/90 tabular-nums">
                      {log.protein || "-"}{" "}
                      {log.protein && (
                        <span className="text-[9px] opacity-40">g</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center font-semibold text-amber-500/90 tabular-nums">
                      {log.carbs || "-"}{" "}
                      {log.carbs && (
                        <span className="text-[9px] opacity-40">g</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center font-semibold text-emerald-500/90 tabular-nums">
                      {log.fats || "-"}{" "}
                      {log.fats && (
                        <span className="text-[9px] opacity-40">g</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center font-medium text-muted-foreground tabular-nums">
                      {log.steps?.toLocaleString() || "-"}
                    </TableCell>
                    <TableCell className="min-w-[200px] whitespace-normal px-4 text-[11px] text-muted-foreground italic">
                      {log.clientNotes || "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
